import { ApiDetails } from '../../../dummyData';

/**
 * Agreement Signature API Service
 *
 * Handles all API calls related to agreement signature workflow for tenants
 * - Tenant signing (no auth required - uses tenant_id verification)
 * - Signature status checking
 */

/**
 * Tenant signs an agreement (no authentication required)
 *
 * @param {number} agreementId - The agreement ID to sign
 * @param {number} tenantId - The tenant ID
 * @param {string} signatureData - Base64 encoded signature image (data:image/png;base64,...)
 * @returns {Promise<Object>} API response
 */
export const tenantSignAgreement = async (agreementId, tenantId, signatureData) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/tenant/sign', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                agreement_id: agreementId,
                tenant_id: tenantId,
                signature_data: signatureData
            })
        });

        const data = await response.json();

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                data: data
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to sign agreement'
            };
        }
    } catch (error) {
        console.error('Error signing agreement (tenant):', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Get signature status for an agreement
 *
 * @param {number} agreementId - The agreement ID
 * @param {string} token - Authentication token
 * @param {boolean} includeSignatures - Whether to include Base64 signature images (default: true)
 * @returns {Promise<Object>} API response with signature status
 */
export const getSignatureStatus = async (agreementId, token, includeSignatures = true) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/signature/status', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                agreement_id: agreementId,
                include_signatures: includeSignatures
            })
        });

        // Handle 403 (unauthorized)
        if (response.status === 403) {
            localStorage.clear();
            window.location.href = '/';
            throw new Error('Session expired. Please log in again.');
        }

        const data = await response.json();

        if (response.status === 200) {
            return {
                success: true,
                data: data
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to fetch signature status'
            };
        }
    } catch (error) {
        console.error('Error fetching signature status:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Validate signature data format
 *
 * @param {string} signatureData - Base64 signature data to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateSignatureData = (signatureData) => {
    if (!signatureData || typeof signatureData !== 'string') {
        return false;
    }

    // Must start with data:image/
    if (!signatureData.startsWith('data:image/')) {
        return false;
    }

    // Must contain base64
    if (!signatureData.includes('base64,')) {
        return false;
    }

    // Check if there's actual data after base64,
    const base64Data = signatureData.split('base64,')[1];
    if (!base64Data || base64Data.length < 10) {
        return false;
    }

    return true;
};

/**
 * Check if tenant can sign the agreement
 *
 * @param {Object} agreement - The agreement object
 * @returns {Object} { canSign: boolean, reason: string }
 */
export const canTenantSignAgreement = (agreement) => {
    if (!agreement) {
        return { canSign: false, reason: 'Agreement not found' };
    }

    // Check if agreement is in correct status
    const validStatuses = ['DRAFT', 'PENDING_SIGNATURE', 'ACTIVE'];
    if (!validStatuses.includes(agreement.status)) {
        return { canSign: false, reason: `Agreement status is ${agreement.status}` };
    }

    // Check if already signed
    if (agreement.tenant_signed) {
        return { canSign: false, reason: 'You have already signed this agreement' };
    }

    // Check if landlord has signed first
    if (!agreement.landlord_signed) {
        return { canSign: false, reason: 'Waiting for landlord to sign first' };
    }

    return { canSign: true, reason: '' };
};

/**
 * Get human-readable signature status for tenant view
 *
 * @param {Object} agreement - The agreement object
 * @returns {string} Status text
 */
export const getTenantSignatureStatusText = (agreement) => {
    if (!agreement) return 'Unknown';

    const landlordSigned = agreement.landlord_signed || false;
    const tenantSigned = agreement.tenant_signed || false;

    if (landlordSigned && tenantSigned) {
        return 'Fully Signed';
    } else if (landlordSigned && !tenantSigned) {
        return 'Ready for Your Signature';
    } else if (!landlordSigned && tenantSigned) {
        return 'Awaiting Landlord Signature';
    } else {
        return 'Not Yet Signed';
    }
};

/**
 * Get next action recommendation for tenant
 *
 * @param {Object} agreement - The agreement object
 * @returns {string} Recommended action
 */
export const getTenantNextAction = (agreement) => {
    if (!agreement) return '';

    const landlordSigned = agreement.landlord_signed || false;
    const tenantSigned = agreement.tenant_signed || false;

    if (landlordSigned && tenantSigned) {
        return 'Agreement is fully signed. You can download it.';
    }

    if (!tenantSigned && landlordSigned) {
        return 'Please sign this agreement to proceed with your tenancy.';
    }

    if (!landlordSigned) {
        return 'Waiting for landlord to sign. You will be notified when it\'s ready.';
    }

    return '';
};
